import axios, { AxiosError } from 'axios'
import type {
  LoginRequest,
  LoginResponse,
  CreateUserRequest,
  CreateUserResponse,
  SystemMetrics,
  StoragePurgeResponse,
  User,
  ApiError,
  LogsResponse
} from '../types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_auth_token')
      localStorage.removeItem('admin_user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', credentials)
    return response.data
  },

  logout: () => {
    localStorage.removeItem('admin_auth_token')
    localStorage.removeItem('admin_user')
  },
}

export const adminApi = {
  // User management
  getUsers: async (): Promise<User[]> => {
    const response = await api.get<{ users: User[], pagination: any }>('/admin/users')
    return response.data.users
  },

  createUser: async (userData: CreateUserRequest): Promise<CreateUserResponse> => {
    const response = await api.post<CreateUserResponse>('/admin/users', userData)
    return response.data
  },

  deleteUser: async (userId: string): Promise<void> => {
    await api.delete(`/admin/users/${userId}`)
  },

  // Storage management
  purgeStorage: async (): Promise<StoragePurgeResponse> => {
    const response = await api.post<StoragePurgeResponse>('/admin/storage/purge')
    return response.data
  },

  // System metrics
  getMetrics: async (): Promise<SystemMetrics> => {
    const response = await api.get<SystemMetrics>('/admin/metrics')
    return response.data
  },

  // Client logs management
  getLogs: async (page: number = 1, limit: number = 50, userId?: string): Promise<LogsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    })
    
    if (userId) {
      params.append('userId', userId)
    }

    const response = await api.get<LogsResponse>(`/admin/logs?${params.toString()}`)
    return response.data
  },

  downloadLog: async (logId: string): Promise<Blob> => {
    const response = await api.get(`/admin/logs/${logId}/download`, {
      responseType: 'blob'
    })
    return response.data
  },
}

export default api